import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { LoadingSkeleton } from '../common/LoadingComponents';
// Fix default marker icons for Leaflet (webpack issue)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// leaflet.heat requires global window.L — must be set before requiring the module
// eslint-disable-next-line @typescript-eslint/no-var-requires
(window as any).L = L;
require('leaflet.heat');

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Interfaces para o componente de mapa
export interface MapMarker {
    id: string;
    position: {
        lat: number;
        lng: number;
    };
    title: string;
    description?: string;
    icon?: string;
    data?: unknown;
}

export interface HeatmapPoint {
    lat: number;
    lng: number;
    weight?: number;
}

export interface LeafletMapComponentProps {
    center: {
        lat: number;
        lng: number;
    };
    zoom?: number;
    markers?: MapMarker[];
    heatmapData?: HeatmapPoint[];
    showHeatmap?: boolean;
    onMarkerClick?: (marker: MapMarker) => void;
    onMapClick?: (event: L.LeafletMouseEvent) => void;
    className?: string;
    style?: React.CSSProperties;
    mapOptions?: Record<string, unknown>;
}

// Create custom icon from URL
function createCustomIcon(iconUrl: string): L.Icon {
    return L.icon({
        iconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
    });
}

// Inner component to handle map click events
function MapClickHandler({ onMapClick }: { onMapClick?: (event: L.LeafletMouseEvent) => void }): JSX.Element | null {
    useMapEvents({
        click(e) {
            onMapClick?.(e);
        },
    });
    return null;
}

// Inner component to recenter the map when center prop changes
function MapRecenter({ center, zoom }: { center: { lat: number; lng: number }; zoom: number }): JSX.Element | null {
    const map = useMap();
    useEffect(() => {
        map.setView([center.lat, center.lng], zoom);
    }, [center.lat, center.lng, zoom, map]);
    return null;
}

// Heatmap layer component using leaflet.heat
function HeatmapLayer({ data }: { data: HeatmapPoint[] }): JSX.Element | null {
    const map = useMap();
    const heatLayerRef = useRef<any>(null);

    useEffect(() => {
        if (!map) return;

        // Remove previous layer first
        if (heatLayerRef.current) {
            try { map.removeLayer(heatLayerRef.current); } catch (_) { }
            heatLayerRef.current = null;
        }

        if (data.length === 0) return;

        const heatPoints = data.map((point) => [
            point.lat,
            point.lng,
            point.weight || 1,
        ]);

        try {
            heatLayerRef.current = (L as any).heatLayer(heatPoints, {
                radius: 25,
                blur: 15,
                maxZoom: 17,
                gradient: {
                    0.0: '#00ff00',
                    0.25: '#7fff00',
                    0.5: '#ffff00',
                    0.75: '#ff7f00',
                    1.0: '#ff0000',
                },
            }).addTo(map);
        } catch (e) {
            console.error('[HeatmapLayer] Failed to create heatLayer:', e);
        }

        return () => {
            if (heatLayerRef.current) {
                try { map.removeLayer(heatLayerRef.current); } catch (_) { }
                heatLayerRef.current = null;
            }
        };
    }, [map, data]);

    return null;
}

/**
 * Componente de Mapa com Leaflet + OpenStreetMap para o painel administrativo
 * Drop-in replacement para o antigo GoogleMapsComponent
 */
export const LeafletMapComponent = memo(({
    center,
    zoom = 11,
    markers = [],
    heatmapData = [],
    showHeatmap = false,
    onMarkerClick,
    onMapClick,
    className = '',
    style = { width: '100%', height: '500px' },
}: LeafletMapComponentProps) => {
    const { t } = useTranslation();
    const [selectedMarker, setSelectedMarker] = useState<MapMarker | null>(null);

    const handleMarkerClick = useCallback((marker: MapMarker) => {
        setSelectedMarker(marker);
        onMarkerClick?.(marker);
    }, [onMarkerClick]);

    return (
        <div className={className} style={style}>
            <MapContainer
                center={[center.lat, center.lng]}
                zoom={zoom}
                style={{ width: '100%', height: '100%' }}
                zoomControl={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />

                <MapRecenter center={center} zoom={zoom} />
                <MapClickHandler onMapClick={onMapClick} />

                {/* Marcadores */}
                {markers.map((marker) => (
                    <Marker
                        key={marker.id}
                        position={[marker.position.lat, marker.position.lng]}
                        icon={marker.icon ? createCustomIcon(marker.icon) : new L.Icon.Default()}
                        eventHandlers={{
                            click: () => handleMarkerClick(marker),
                        }}
                    >
                        <Popup>
                            <div className="p-2">
                                <h3 className="font-semibold text-gray-800">{marker.title}</h3>
                                {marker.description && (
                                    <p className="text-gray-600 text-sm mt-1">{marker.description}</p>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {/* Mapa de calor */}
                {showHeatmap && heatmapData.length > 0 && (
                    <HeatmapLayer data={heatmapData} />
                )}
            </MapContainer>
        </div>
    );
});

LeafletMapComponent.displayName = 'LeafletMapComponent';

// Re-export MapMarker for backward compatibility
export default LeafletMapComponent;
